import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Stack,
  Card,
  CardContent,
  Tooltip,
  Checkbox,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  styled
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DownloadIcon from '@mui/icons-material/Download';
import QrCodeIcon from '@mui/icons-material/QrCode';
import QuestionModal from './components/QuestionModal';
import SurveyReports from './components/SurveyReports';
import QRMenuFeedbackList from './components/QRMenuFeedbackList';
import SurveyCharts from '../components/dashboard/SurveyCharts';
import AddTaskModal from '../components/tasks/modals/AddTaskModal';
import QrPrintModal from '../components/tasks/modals/QrPrintModal';
import GuideButton from '../components/common/guide/GuideButton';
import { useAuth } from '../contexts/AuthContext';
import { taskDataService, stringifyStringArray, normalizeBranchesId } from '../services/taskDataService';
import { surveyDataService } from '../services/surveyDataService';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import firebaseDataService from '../services/FirebaseDataService';
import { useTranslation } from 'react-i18next';
import NotificationService from '../services/NotificationService';

// Kaydırılabilir ana içerik için styled component
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto',
  height: 'calc(100vh - 64px)', // Header yüksekliğini çıkarıyoruz
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
}));

// TabPanel fonksiyonu
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Surveys bileşeni için prop tipi
interface SurveysProps {
  isManager?: boolean;
  branchId?: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`survey-tabpanel-${index}`}
      aria-labelledby={`survey-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `survey-tab-${index}`,
    'aria-controls': `survey-tabpanel-${index}`,
  };
}

interface AnswerItem {
  text: string;
  type: 'positive' | 'negative' | 'neutral';
}

interface Survey {
  id: string;
  /** SQL int birincil anahtar (update/delete/goreve-baglama icin). */
  sqlId: number;
  legacyFirebaseKey?: string | null;
  title: string;
  answers: AnswerItem[];
  createdAt: number;
  createdBy: string;
  tasks?: string[];
  branchId?: string;
}

// Eski format için geriye uyumluluk interface'i
interface LegacySurvey {
  id: string;
  title: string;
  questions: {
    [key: string]: {
      title: string;
      answers: string[];
      createdAt: number;
      createdBy: string;
    };
  };
  createdAt: number;
  tasks?: string[];
}

interface EditingQuestion {
  id: string;
  title: string;
  answers: AnswerItem[];
}

interface Task {
  id: string;
  name: string;
  description: string;
  createdAt: number;
  branchId?: string;
  branchesId?: string | {[key: string]: boolean};
  isRecurring?: boolean;
  repeatType?: string;
}

const Surveys: React.FC<SurveysProps> = ({ isManager = false, branchId }) => {
  const { t } = useTranslation('common2');
  const { currentUser, getBranchName: getAuthBranchName, userDetails } = useAuth(); // ✅ userDetails eklendi
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [allSurveys, setAllSurveys] = useState<Survey[]>([]); // Orijinal tüm anketler
  const [surveys, setSurveys] = useState<Survey[]>([]); // Görüntülenen anketler (filtrelenmiş)
  const [loading, setLoading] = useState(true);
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingQuestions, setEditingQuestions] = useState<EditingQuestion[]>([]);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  // const [branchNames, setBranchNames] = useState<{[key: string]: string}>({}); // ❌ Kaldırıldı - AuthContext'ten geliyor
  
  // Tab state'i
  const [tabValue, setTabValue] = useState(0);

  // Şube görevlerini filtreleme için state
  const [branchTasks, setBranchTasks] = useState<Task[]>([]);
  
  // Anket listesini yeniden yüklemek için sayaç (yeni anket eklendiğinde artırılır)
  const [surveyRefreshNonce, setSurveyRefreshNonce] = useState(0);
  
  // AddTaskModal için state'ler
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [personnel, setPersonnel] = useState<any[]>([]);
  const [taskGroups, setTaskGroups] = useState<any[]>([]);
  
  // ✅ CompanyId'yi userDetails'ten direkt al (cache'li)
  const companyId = userDetails?.companyId || '';
  
  // QR Print Modal için state'ler
  const [qrPrintModalOpen, setQrPrintModalOpen] = useState(false);
  const [selectedTaskForQr, setSelectedTaskForQr] = useState<any | null>(null);
  
  
  // Tab değişimini yönet
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Şube adlarını ve company bilgilerini yükle
  useEffect(() => {
    if (!currentUser || !companyId) return;

    const fetchBranchNames = async () => {
      try {
        // ✅ CompanyId artık userDetails'ten geliyor, getUserData çağrısı kaldırıldı
        // ✅ Şube isimleri artık AuthContext'ten geliyor (getBranchName fonksiyonu)
        // Personel ve görev gruplarını yükle (SQL-once via FirebaseDataService)
        const personnelData = await firebaseDataService.getPersonnel(companyId);
        if (personnelData) {
          const personnelList = Array.isArray(personnelData) ? personnelData : [personnelData];
          setPersonnel(personnelList);
        }

        // Faz 5 DILIM 8: görev grupları SQL'den (taskDataService). id=legacyFirebaseKey, sqlId ayrı.
        const taskGroupsList = await taskDataService.listGroupsLegacy();
        setTaskGroups(taskGroupsList);
      } catch (error) {
        console.error('Şube isimleri yüklenirken hata:', error);
      }
    };

    fetchBranchNames();
  }, [currentUser, companyId, t]);
  
  // ✅ Şube adını almak için AuthContext'ten gelen fonksiyonu kullan
  const getBranchName = (branchId: string | undefined): string => {
    if (!branchId) return t('surveys.branchInfo.unspecifiedBranch');
    const name = getAuthBranchName(branchId);
    return name !== '-' && name !== 'Bilinmeyen Şube' ? name : t('surveys.branchInfo.unspecifiedBranch');
  };

  // Anketleri yükle (TEK SEFERLİK)
  useEffect(() => {
    if (!currentUser || !companyId) return;

    const fetchUserData = async () => {
      try {
        // Faz 8: anketler SQL'den (surveyDataService). id = String(sqlId);
        // goreve-baglama referansi survey.tasks (taskFirebaseKey) ile korunur.
        const surveyDtos = await surveyDataService.listAllSurveys();
        const surveysList: Survey[] = surveyDtos.map((s) => ({
          id: s.id,
          sqlId: s.sqlId,
          legacyFirebaseKey: s.legacyFirebaseKey,
          title: s.title,
          answers: s.answers,
          createdAt: s.createdAt,
          createdBy: s.createdBy || '',
          tasks: s.tasks,
          branchId: s.branchId || undefined,
        }));

        surveysList.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setAllSurveys(surveysList);
        setLoading(false);
      } catch (error) {
        console.error('Anketler yüklenirken hata:', error);
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser, companyId, isManager, branchId, surveyRefreshNonce]);

  // Görevleri yükle (TEK SEFERLİK)
  useEffect(() => {
    if (!currentUser || !companyId) return;

    const fetchUserData = async () => {
      try {
        // ✅ CompanyId artık userDetails'ten geliyor, getUserData çağrısı kaldırıldı

        // Faz 5 DILIM 8: görevler SQL'den (taskDataService). Anket atama referansı
        // legacyFirebaseKey ile korunur (id = legacyFirebaseKey || String(sqlId)).
        const taskItems: any[] = [];
        let tpage = 1;
        for (;;) {
          const res = await taskDataService.listTasks({ page: tpage, pageSize: 500 });
          taskItems.push(...res.items);
          if (res.items.length === 0 || taskItems.length >= (res.totalCount || 0)) break;
          tpage++;
        }

        if (taskItems.length > 0) {
          const tasksArray = taskItems.map((task: any) => ({
            id: task.legacyFirebaseKey || String(task.id),
            name: task.name,
            description: task.description,
            createdAt: task.createdAt ? new Date(task.createdAt).getTime() : 0,
            branchId: normalizeBranchesId(task.branchesId) || undefined,
            branchesId: normalizeBranchesId(task.branchesId) || undefined,
            isRecurring: task.isRecurring,
            repeatType: task.taskType
          }));
          
          if (isManager && branchId) {
            const filteredTasks = tasksArray.filter(task => {
              if (typeof task.branchesId === 'object' && task.branchesId !== null) {
                return Object.keys(task.branchesId).includes(branchId);
              } else if (task.branchesId === branchId) {
                return true;
              } else if (task.branchId === branchId) {
                return true;
              }
              return false;
            });
            
            console.log(`Şube ID: ${branchId} için ${filteredTasks.length} görev bulundu`);
            setTasks(filteredTasks);
            setBranchTasks(filteredTasks);
          } else {
            setTasks(tasksArray);
            setBranchTasks(tasksArray);
          }
        } else {
          setTasks([]);
          setBranchTasks([]);
        }
      } catch (error) {
        console.error('Görevler yüklenirken hata:', error);
      }
    };

    fetchUserData();
  }, [currentUser, companyId, isManager, branchId]);

  // Manager için anket filtreleme
  useEffect(() => {
    if (isManager && branchId) {
      console.log(`Manager için anketler filtreleniyor. Şube ID: ${branchId}, Toplam anket: ${allSurveys.length}, Şube görev: ${branchTasks.length}`);
      
      if (allSurveys.length === 0) {
        setSurveys([]);
        return;
      }
      
      // Şube görevlerinin ID'lerini al
      const branchTaskIds = branchTasks.map(task => task.id);
      
      // Anketleri filtrele - ya doğrudan şube ID'si ile eşleşenler ya da şube görevlerine bağlı olanlar
      const filteredSurveys = allSurveys.filter(survey => {
        // 1. Doğrudan şube ID'si ile eşleşme
        if (survey.branchId && survey.branchId === branchId) {
          console.log(`✅ Anket '${survey.title}' doğrudan şube ID'si ile eşleşiyor: ${survey.branchId}`);
          return true;
        }
        
        // 2. Görevler aracılığıyla eşleşme kontrolü
        if (survey.tasks && survey.tasks.length > 0 && branchTaskIds.length > 0) {
          // Anketin görevlerinden en az biri şube görevlerinde varsa anketi göster
          const hasMatchingTask = survey.tasks.some(taskId => branchTaskIds.includes(taskId));
          if (hasMatchingTask) {
            console.log(`✅ Anket '${survey.title}' görev aracılığıyla eşleşiyor. Anket görevleri:`, survey.tasks);
            return true;
          }
        }
        
        return false;
      });
      
      console.log(`🎯 Manager filtreleme sonucu: ${filteredSurveys.length}/${allSurveys.length} anket gösterilecek`);
      setSurveys(filteredSurveys);
    } else {
      // Manager değilse veya branchId yoksa tüm anketleri göster
      setSurveys(allSurveys);
    }
  }, [isManager, branchId, allSurveys, branchTasks]);

  const handleOpenQuestionModal = () => {
    setIsQuestionModalOpen(true);
  };

  const handleCloseQuestionModal = () => {
    setIsQuestionModalOpen(false);
  };

  // AddTaskModal handler'ları
  const handleOpenAddTaskModal = () => {
    setIsAddTaskModalOpen(true);
  };

  const handleCloseAddTaskModal = () => {
    setIsAddTaskModalOpen(false);
  };

  const handleAddTask = async (taskData: any) => {
    try {
      console.log('Surveys sayfasından yeni görev ekleniyor:', taskData);
      
      if (!companyId || !currentUser) {
        console.error('Company ID veya kullanıcı bilgisi eksik');
        return;
      }

      // Faz 5 DILIM 8: görev SQL'e (taskDataService). groupId legacyFirebaseKey → sqlId.
      const groupSqlId = taskData.groupId
        ? (taskGroups.find((g: any) => g.id === taskData.groupId)?.sqlId ?? null)
        : null;

      if (taskData.isRecurring && taskData.repeatType === 'monthly' && taskData.monthlyData) {
        const createdMonthly = await taskDataService.createTask({
          taskType: 'monthly',
          name: taskData.name,
          description: taskData.description || null,
          personnelFirebaseUid: taskData.personnelId || null,
          branchesId: taskData.branchesId || null,
          groupId: groupSqlId,
          isRecurring: true,
          completionType: taskData.completionType || 'button',
          startTolerance: taskData.startTolerance ?? 15,
          requestCompletionPhoto: false,
          canBeRejected: false,
          createdByUid: currentUser.uid || null,
        });
        const monthlySlots = taskData.monthlyData.flatMap((monthData: any) =>
          monthData.days.map((dayData: any) => ({
            monthNumber: monthData.month,
            dayOfMonth: dayData.day,
            dailyRepetitions: dayData.dailyRepetitions ?? 1,
            repetitionTimes: stringifyStringArray(dayData.repetitionTimes ?? []),
          }))
        );
        if (monthlySlots.length > 0) {
          await taskDataService.setSlots(createdMonthly.id, monthlySlots);
        }
        console.log('Anket görevi (aylık) SQL\'e eklendi, ID:', createdMonthly.id);
      } else {
        const created = await taskDataService.createTask({
          taskType: taskData.isRecurring ? 'daily' : 'single',
          name: taskData.name,
          description: taskData.description || null,
          personnelFirebaseUid: taskData.personnelId || null,
          branchesId: taskData.branchesId || null,
          groupId: groupSqlId,
          isRecurring: !!taskData.isRecurring,
          completionType: taskData.completionType || 'button',
          startTolerance: taskData.isRecurring ? (taskData.startTolerance ?? 15) : null,
          dailyRepetitions: taskData.isRecurring ? (taskData.dailyRepetitions ?? null) : null,
          repetitionTimes: taskData.isRecurring ? stringifyStringArray(taskData.repetitionTimes ?? []) : null,
          requestCompletionPhoto: false, // Surveys'ten eklenen görevlerde false
          canBeRejected: false, // Surveys'ten eklenen görevlerde false
          deadline: taskData.deadline || null,
          startDate: taskData.startDate || null,
          imageUrls: taskData.imageUrls && taskData.imageUrls.length > 0
            ? stringifyStringArray(taskData.imageUrls)
            : null,
          createdByUid: currentUser.uid || null,
        });
        console.log('Anket görevi SQL\'e eklendi, ID:', created.id);
      }

      // Bildirim gönder (kullanıcıya) — .NET notification API (tek çağrı: kaydet + FCM gönder)
      if (taskData.personnelId) {
        try {
          await NotificationService.sendNotification(companyId, [taskData.personnelId], 'Yeni görevin var', taskData.name);
          console.log('Bildirim başarıyla gönderildi');
        } catch (notifError) {
          console.error('Bildirim gönderilirken hata:', notifError);
        }
      }

      // Modalı kapat
      handleCloseAddTaskModal();
      
    } catch (error) {
      console.error('Görev eklenirken hata:', error);
    }
  };

  const handleRowClick = (survey: Survey) => {
    setSelectedSurvey(survey);
    setIsDetailModalOpen(true);
  };

  const handleEditMode = () => {
    if (selectedSurvey) {
      setEditingQuestions([{
        id: 'main',
        title: selectedSurvey.title,
        answers: [...selectedSurvey.answers]
      }]);
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSaveChanges = async () => {
    if (!selectedSurvey || !currentUser || !companyId) return;

    try {
      const updatedQuestion = editingQuestions[0]; // Artık tek bir soru var

      // Faz 8: anket guncelleme SQL'e (surveyDataService). Answers verilince secenekler yeniden yazilir.
      await surveyDataService.updateSurvey({
        id: selectedSurvey.sqlId,
        title: updatedQuestion.title,
        answers: updatedQuestion.answers,
      });

      // Seçili anketin ve listenin yerel durumunu güncelle
      const updated = { ...selectedSurvey, title: updatedQuestion.title, answers: updatedQuestion.answers };
      setSelectedSurvey(updated);
      setAllSurveys(prev => prev.map(s => s.sqlId === selectedSurvey.sqlId ? updated : s));

      setIsEditing(false);
    } catch (error) {
      console.error('Değişiklikler kaydedilirken hata:', error);
    }
  };

  const handleQuestionChange = (questionId: string, newTitle: string) => {
    setEditingQuestions(prev => 
      prev.map(q => q.id === questionId ? { ...q, title: newTitle } : q)
    );
  };

  const handleAnswerChange = (questionId: string, answerIndex: number, newAnswer: string) => {
    setEditingQuestions(prev => 
      prev.map(q => {
        if (q.id === questionId) {
          const newAnswers = [...q.answers];
          newAnswers[answerIndex] = { 
            ...newAnswers[answerIndex], 
            text: newAnswer 
          };
          return { ...q, answers: newAnswers };
        }
        return q;
      })
    );
  };

  const handleAnswerTypeChange = (questionId: string, answerIndex: number, newType: 'positive' | 'negative' | 'neutral') => {
    setEditingQuestions(prev => 
      prev.map(q => {
        if (q.id === questionId) {
          const newAnswers = [...q.answers];
          newAnswers[answerIndex] = { 
            ...newAnswers[answerIndex], 
            type: newType 
          };
          return { ...q, answers: newAnswers };
        }
        return q;
      })
    );
  };

  const handleAddAnswer = (questionId: string) => {
    setEditingQuestions(prev => 
      prev.map(q => q.id === questionId ? { 
        ...q, 
        answers: [...q.answers, { text: '', type: 'neutral' }] 
      } : q)
    );
  };

  const handleRemoveAnswer = (questionId: string, answerIndex: number) => {
    setEditingQuestions(prev => 
      prev.map(q => {
        if (q.id === questionId) {
          const newAnswers = [...q.answers];
          newAnswers.splice(answerIndex, 1);
          return { ...q, answers: newAnswers };
        }
        return q;
      })
    );
  };

  const handleCloseDetailModal = () => {
    setSelectedSurvey(null);
    setIsDetailModalOpen(false);
    setIsEditing(false);
    setEditingQuestions([]);
  };

  const handleDeleteSurvey = async () => {
    if (!selectedSurvey || !currentUser || !companyId) return;

    try {
      setIsDeleting(true);

      const userChoice = window.confirm(
        `"${selectedSurvey.title}" anketini silmek istediğinize emin misiniz?\n\n` +
        `Bu işlem geri alınamaz.`
      );

      if (!userChoice) {
        setIsDeleting(false);
        return;
      }

      // Faz 8: anket silme SQL'e (soft-delete). Yanitlar SQL'de korunur; rapor uclari silinmis anketi gizler.
      await surveyDataService.deleteSurvey(selectedSurvey.sqlId);
      setAllSurveys(prev => prev.filter(s => s.sqlId !== selectedSurvey.sqlId));
      handleCloseDetailModal();
    } catch (error) {
      console.error('Anket silinirken hata:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Görev seçme modalını aç
  const handleOpenTaskModal = () => {
    if (selectedSurvey) {
      console.log(`Görev seçme modalı açılıyor, mevcut görevler:`, selectedSurvey.tasks);
      console.log(`Kullanılabilir görevler:`, branchTasks);
      setSelectedTasks(selectedSurvey.tasks || []);
    }
    setIsTaskModalOpen(true);
  };

  // Görev seçme modalını kapat
  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false);
    setSelectedTasks([]);
  };

  // Görev seçme/kaldırma
  const handleTaskToggle = (taskId: string) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  // Seçilen görevleri kaydet
  const handleSaveTasks = async () => {
    if (!currentUser || !selectedSurvey) return;

    try {
      // Faz 8: anket-görev bağları SQL'e (surveyDataService). selectedTasks = taskFirebaseKey[].
      await surveyDataService.linkSurveyTasks(selectedSurvey.sqlId, selectedTasks);

      // Seçili anketi ve listeyi güncelle
      const updated = { ...selectedSurvey, tasks: selectedTasks };
      setSelectedSurvey(updated);
      setAllSurveys(prev => prev.map(s => s.sqlId === selectedSurvey.sqlId ? { ...s, tasks: selectedTasks } : s));

      handleCloseTaskModal();
    } catch (error) {
      console.error('❌ Görevler kaydedilirken hata oluştu:', error);
      alert('Hata: Görevler kaydedilemedi. Lütfen tekrar deneyin.');
    }
  };

  // QR Kod yazdırma modalını açma işlevi
  const handleOpenQrPrintModal = (task: any, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Kart tıklama olayının tetiklenmesini engelle
    }
    setSelectedTaskForQr(task);
    setQrPrintModalOpen(true);
  };

  // QR Kod yazdırma modalını kapatma işlevi
  const handleCloseQrPrintModal = () => {
    setQrPrintModalOpen(false);
    setSelectedTaskForQr(null);
  };

  // Görev silme fonksiyonu
  const handleDeleteTask = async (taskId: string) => {
    if (!selectedSurvey || !currentUser || !companyId) return;

    try {
      // Görevi anketten kaldır (SQL'de bağları yeniden yaz).
      const updatedTasks = selectedSurvey.tasks?.filter(id => id !== taskId) || [];
      await surveyDataService.linkSurveyTasks(selectedSurvey.sqlId, updatedTasks);

      // Seçili anketi ve listeyi güncelle
      setSelectedSurvey(prev => prev ? { ...prev, tasks: updatedTasks } : null);
      setAllSurveys(prev => prev.map(s => s.sqlId === selectedSurvey.sqlId ? { ...s, tasks: updatedTasks } : s));
    } catch (error) {
      console.error('Görev silinirken hata oluştu:', error);
    }
  };

  // Excel dışa aktarma fonksiyonu
  const exportToExcel = async () => {
    try {
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Anketler');
      
      // Sütun başlıklarını tanımla
      worksheet.columns = [
        { header: t('surveys.list.columns.question'), key: 'title', width: 40 },
        { header: t('surveys.list.columns.answers'), key: 'answers', width: 50 },
        { header: t('surveys.detail.tasks'), key: 'tasks', width: 30 }
      ];
      
      // Stil tanımlamaları
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      // Başlık stilini uygula
      worksheet.getRow(1).eachCell(cell => {
        cell.style = headerStyle;
      });
      
      // Anket verilerini ekle
      surveys.forEach(survey => {
        // Cevap metinlerini birleştir
        const answersText = survey.answers.map(answer => {
          return `${answer.text} (${answer.type === 'positive' ? t('surveys.edit.answerTypes.positive') : answer.type === 'negative' ? t('surveys.edit.answerTypes.negative') : t('surveys.edit.answerTypes.neutral')})`;
        }).join(', ');
        
        // Bağlı görevleri bul
        const taskNames = survey.tasks && survey.tasks.length > 0 
          ? survey.tasks.map(taskId => {
              const task = tasks.find(t => t.id === taskId);
              return task ? task.name : 'Bilinmeyen Görev';
            }).join(', ')
          : 'Görev yok';
        
        // Oluşturulma tarihi
        const createdAtDate = new Date(survey.createdAt).toLocaleDateString('tr-TR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        
        // Satır ekleme
        worksheet.addRow({
          title: survey.title,
          answers: answersText,
          tasks: taskNames
        });
      });
      
      // Zebrastripe (alternatif satır renklendirme)
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) { // başlığı atla
          const fillColor = rowNumber % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
          row.eachCell(cell => {
            cell.style = {
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
              border: {
                top: { style: 'thin' as const },
                left: { style: 'thin' as const },
                bottom: { style: 'thin' as const },
                right: { style: 'thin' as const }
              }
            };
          });
        }
      });
      
      // Excel dosyasını oluştur ve indir
      const buffer = await workbook.xlsx.writeBuffer();
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      saveAs(new Blob([buffer]), `Anketler_${currentDate}.xlsx`);
      
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      alert(t('surveys.excel.error'));
    }
  };

  // Görev seçme modalı içeriğini güncelle
  const renderTaskSelection = () => {
    // Şube bazlı filtreleme
    const allDisplayTasks = isManager && branchId ? branchTasks : tasks;
    
    // Sadece günlük, haftalık ve aylık tekrarlı görevleri filtrele ve en yeni görev en başta gelecek şekilde sırala
    const recurringTasks = allDisplayTasks
      .filter(task => {
        return task.isRecurring && 
               task.repeatType && 
               ['daily', 'weekly', 'monthly'].includes(task.repeatType);
      })
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)); // En yeni görev en başta
    
    if (recurringTasks.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {isManager 
              ? t('surveys.taskModal.noBranchRecurringTasks')
              : t('surveys.taskModal.noRecurringTasks')}
          </Typography>
        </Box>
      );
    }
    
    return (
      <List>
        {recurringTasks.map((task) => (
          <React.Fragment key={task.id}>
            <ListItem>
              <ListItemText
                primary={task.name}
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {task.description}
                    </Typography>
                    <Typography variant="caption" color="primary">
                      {task.repeatType === 'daily' && 'Günlük Tekrarlı'}
                      {task.repeatType === 'weekly' && 'Haftalık Tekrarlı'}
                      {task.repeatType === 'monthly' && 'Aylık Tekrarlı'}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Checkbox
                  edge="end"
                  checked={selectedTasks.includes(task.id)}
                  onChange={() => handleTaskToggle(task.id)}
                />
              </ListItemSecondaryAction>
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <ScrollableContent>
      <Box sx={{ pt: 1, pb: 3 }}>
        {/* Tab Başlıkları */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="anket yönetimi tabları"
          >
            <Tab label="Anket Grafikleri" {...a11yProps(0)} />
            <Tab label={t('surveys.tabs.surveys')} {...a11yProps(1)} />
            <Tab label={t('surveys.tabs.reports')} {...a11yProps(2)} />
            <Tab label="QR Menü Geri Bildirimleri" {...a11yProps(3)} />
          </Tabs>
        </Box>

        {/* Tab İçerikleri */}
        <TabPanel value={tabValue} index={0}>
          {/* Anket Grafikleri Tab */}
          <Box sx={{ 
            backgroundColor: 'background.paper',
            p: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          }}>
            <Typography variant="h5" component="h1" fontWeight="bold" color="primary" sx={{ mb: 3 }}>
              Anket Grafikleri ve İstatistikleri
            </Typography>
            <SurveyCharts 
              branchId={branchId} 
              isManager={isManager} 
              selectedWidgets={['overallSurveySatisfaction', 'surveyTrend', 'surveyDetailedStats']}
              showBranchFilter={true}
            />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Sayfanın en üst kısmı - başlık ve kontroller */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3, 
            mt: 1,
            backgroundColor: 'background.paper',
            p: 2,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          }}>
            <Typography variant="h5" component="h1" fontWeight="bold" color="primary">
              {t('surveys.title')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <GuideButton
                guideKey="surveys.list"
                sx={{
                  bgcolor: 'info.light',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'info.main',
                  }
                }}
              />
              <Button
                variant="contained"
                color="info"
                startIcon={<DownloadIcon />}
                onClick={exportToExcel}
                disabled={loading || surveys.length === 0}
              >
                {t('surveys.actions.downloadExcel')}
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<AddIcon />}
                onClick={handleOpenAddTaskModal}
                sx={{ borderRadius: 2 }}
              >
                {t('surveys.actions.addNewSurveyButton')}
              </Button>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleOpenQuestionModal}
                sx={{ borderRadius: 2 }}
              >
                {t('surveys.actions.addNewSurvey')}
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* Sol Taraf - Anket Listesi */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" component="h1">
                    {t('surveys.list.title')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button 
                      variant="outlined" 
                      startIcon={<AddIcon />}
                      onClick={handleOpenAddTaskModal}
                      size="small"
                    >
                      {t('surveys.actions.addNewSurveyButton')}
                    </Button>
                    <Button 
                      variant="contained" 
                      startIcon={<AddIcon />}
                      onClick={handleOpenQuestionModal}
                    >
                      {t('surveys.actions.addNewSurvey')}
                    </Button>
                  </Box>
                </Box>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : surveys.length === 0 ? (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    {t('surveys.list.noSurveys')}
                  </Typography>
                ) : (
                  <TableContainer sx={{ 
                    maxHeight: 'calc(100vh - 240px)', // Tablonun maksimum yüksekliği
                    overflowY: 'auto'
                  }}>
                    <Table size="medium">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('surveys.list.columns.question')}</TableCell>
                          <TableCell>{t('surveys.list.columns.answers')}</TableCell>
                          {!isManager && <TableCell>{t('surveys.list.columns.branch')}</TableCell>}
                          <TableCell align="center">Görevler</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {surveys.map((survey) => {
                          return (
                            <TableRow 
                              key={survey.id} 
                              hover 
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell onClick={() => handleRowClick(survey)}>
                                <Typography variant="body2">
                                  {survey.title}
                                </Typography>
                              </TableCell>
                              <TableCell onClick={() => handleRowClick(survey)}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {survey.answers.map((answer, index) => (
                                    <Chip
                                      key={index}
                                      label={answer.text}
                                      size="small"
                                      color={answer.type === 'positive' ? 'success' : answer.type === 'negative' ? 'error' : 'default'}
                                    />
                                  ))}
                                </Box>
                              </TableCell>
                              {!isManager && (
                                <TableCell onClick={() => handleRowClick(survey)}>
                                  {survey.branchId ? (
                                    <Chip
                                      label={getBranchName(survey.branchId)}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      {t('surveys.branchInfo.generalSurvey')}
                                    </Typography>
                                  )}
                                </TableCell>
                              )}
                              <TableCell align="center" onClick={() => handleRowClick(survey)}>
                                {survey.tasks && survey.tasks.length > 0 ? (
                                  <Chip
                                    label={`${survey.tasks.length} görev`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    Görev yok
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>

            {/* Sağ Taraf - Görev Listesi */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h5" component="h2" gutterBottom>
                  {t('surveys.tasks.title')}
                </Typography>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer sx={{ 
                    maxHeight: 'calc(100vh - 240px)',
                    overflowY: 'auto'
                  }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('surveys.tasks.columns.taskName')}</TableCell>
                          <TableCell>{t('surveys.tasks.columns.surveyName')}</TableCell>
                          <TableCell align="center">İşlemler</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {surveys.map((survey) => (
                          survey.tasks && survey.tasks.map((taskId) => {
                            const task = tasks.find(t => t.id === taskId);
                            return task ? (
                              <TableRow key={`${survey.id}-${taskId}`}>
                                <TableCell>
                                  <Typography variant="body2">
                                    {task.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {survey.title}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="QR Kod Yazdır">
                                    <IconButton 
                                      size="small" 
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenQrPrintModal(task);
                                      }}
                                    >
                                      <QrCodeIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ) : null;
                          })
                        ))}
                        {surveys.every(survey => !survey.tasks || survey.tasks.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <Typography variant="body2" color="text.secondary" align="center">
                                {t('surveys.tasks.noTasks')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <SurveyReports 
            isManager={isManager} 
            branchId={branchId} 
            companyId={companyId}
            surveys={surveys}
            allSurveys={allSurveys}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <QRMenuFeedbackList />
        </TabPanel>
      </Box>

      {/* Anket Ekleme Modalı */}
      <QuestionModal
        open={isQuestionModalOpen}
        onClose={handleCloseQuestionModal}
        isManager={isManager}
        branchId={branchId}
        onSaved={() => setSurveyRefreshNonce(n => n + 1)}
      />

      {/* Görev Ekleme Modalı */}
      <AddTaskModal
        open={isAddTaskModalOpen}
        onClose={handleCloseAddTaskModal}
        onAddTask={handleAddTask}
        personnel={personnel}
        taskGroups={taskGroups}
        companyId={companyId}
        branchId={branchId}
        isManager={isManager}
        title="Yeni Anket Ekle"
        isFromSurveys={true}
      />

      {/* Anket Detay Modalı */}
      <Dialog
        open={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Box>
            <Typography variant="h6" component="div">
              {selectedSurvey?.title}
            </Typography>
            {selectedSurvey?.branchId && (
              <Typography variant="caption" color="text.secondary">
                {getBranchName(selectedSurvey.branchId)}
              </Typography>
            )}
          </Box>
          <Box>
            <Tooltip title={t('surveys.detail.actions.addTask')}>
              <IconButton 
                onClick={handleOpenTaskModal}
                color="primary"
                sx={{ mr: 1 }}
              >
                <AssignmentIcon />
              </IconButton>
            </Tooltip>
            {!isEditing ? (
              <>
                <Tooltip title={t('surveys.detail.actions.edit')}>
                  <IconButton 
                    onClick={handleEditMode}
                    color="primary"
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('surveys.detail.actions.delete')}>
                  <IconButton 
                    onClick={handleDeleteSurvey}
                    color="error"
                    disabled={isDeleting}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : null}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedSurvey && (
            <Stack spacing={3}>
              {/* Sorular Bölümü */}
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                  {t('surveys.detail.questions')}
                </Typography>
                {isEditing ? (
                  // Düzenleme Modu
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Stack spacing={2}>
                        <TextField
                          label={t('surveys.edit.question')}
                          value={editingQuestions[0].title}
                          onChange={(e) => handleQuestionChange(editingQuestions[0].id, e.target.value)}
                          fullWidth
                          size="small"
                        />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {t('surveys.edit.answers')}
                          </Typography>
                          <Stack spacing={1}>
                            {editingQuestions[0].answers.map((answer, answerIndex) => (
                              <Box key={answerIndex} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField
                                  value={answer.text}
                                  onChange={(e) => handleAnswerChange(editingQuestions[0].id, answerIndex, e.target.value)}
                                  size="small"
                                  fullWidth
                                />
                                <FormControl sx={{ minWidth: 130 }}>
                                  <InputLabel size="small">{t('surveys.edit.status')}</InputLabel>
                                  <Select
                                    value={answer.type}
                                    label={t('surveys.edit.status')}
                                    onChange={(e) => handleAnswerTypeChange(editingQuestions[0].id, answerIndex, e.target.value as 'positive' | 'negative' | 'neutral')}
                                    size="small"
                                  >
                                    <MenuItem value="positive">
                                      <Chip label={t('surveys.edit.answerTypes.positive')} color="success" size="small" />
                                    </MenuItem>
                                    <MenuItem value="negative">
                                      <Chip label={t('surveys.edit.answerTypes.negative')} color="error" size="small" />
                                    </MenuItem>
                                    <MenuItem value="neutral">
                                      <Chip label={t('surveys.edit.answerTypes.neutral')} color="default" size="small" />
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleRemoveAnswer(editingQuestions[0].id, answerIndex)}
                                  disabled={editingQuestions[0].answers.length <= 1}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Box>
                            ))}
                            <Button
                              startIcon={<AddIcon />}
                              onClick={() => handleAddAnswer(editingQuestions[0].id)}
                              size="small"
                            >
                              {t('surveys.edit.addAnswer')}
                            </Button>
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ) : (
                  // Görüntüleme Modu
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Stack spacing={2}>
                        <Typography variant="subtitle1">
                          {selectedSurvey?.title}
                        </Typography>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {t('surveys.detail.answers')}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selectedSurvey?.answers?.map((answer, index) => (
                              <Chip
                                key={index}
                                label={answer.text}
                                size="small"
                                color={answer.type === 'positive' ? 'success' : answer.type === 'negative' ? 'error' : 'default'}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                )}
              </Box>

              {/* Görevler Bölümü */}
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                  {t('surveys.detail.tasks')}
                </Typography>
                {selectedSurvey.tasks && selectedSurvey.tasks.length > 0 ? (
                  <Stack spacing={1}>
                    {selectedSurvey.tasks.map((taskId) => {
                      const task = tasks.find(t => t.id === taskId);
                      return task ? (
                        <Card key={taskId} variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Stack spacing={1} sx={{ flex: 1 }}>
                                <Typography variant="subtitle1">
                                  {task.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {task.description}
                                </Typography>
                              </Stack>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="QR Kod Yazdır">
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={(e) => handleOpenQrPrintModal(task, e)}
                                  >
                                    <QrCodeIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('surveys.detail.actions.delete')}>
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleDeleteTask(taskId)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ) : null;
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('surveys.detail.noTasks')}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {isEditing ? (
            <>
              <Button onClick={handleCancelEdit} variant="outlined">
                {t('surveys.detail.actions.cancel')}
              </Button>
              <Button 
                onClick={handleSaveChanges} 
                variant="contained"
                startIcon={<SaveIcon />}
              >
                {t('surveys.detail.actions.save')}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseDetailModal} variant="outlined">
              {t('surveys.detail.actions.close')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Görev Seçme Modalı */}
      <Dialog
        open={isTaskModalOpen}
        onClose={handleCloseTaskModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('surveys.taskModal.title')}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {renderTaskSelection()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTaskModal} variant="outlined">
            {t('surveys.taskModal.actions.cancel')}
          </Button>
          <Button onClick={handleSaveTasks} variant="contained">
            {t('surveys.taskModal.actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Kod Yazdırma Modalı */}
      <QrPrintModal
        open={qrPrintModalOpen}
        onClose={handleCloseQrPrintModal}
        task={selectedTaskForQr}
      />
    </ScrollableContent>
  );
};

export default Surveys; 